import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  CcfToast,
  updateActiveContact,
} from '@nice-ccf/ui-components';
import styles from './ccf-consumer-card-details-tab.module.scss';
import {
  CcfTypography,
  CcfBox,
  CcfButton,
  CcfStrokeIcon,
  CcfUpArrowIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';

interface CcfConsumerCardDetailsTabCustomOthersRowProps {
  headerCaption: string;
  detailsMinValue: boolean;
}

/** * Handles the click event when the edit button is clicked.
 * Sets the `isEditing` state to `true`.
 * @example
 */
function CcfConsumerCardDetailsTabCustomOthersRow(
  props: CcfConsumerCardDetailsTabCustomOthersRowProps
) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [guarantorAsPrimary, setGuarantorAsPrimary] = useState(true);
  const activeContact = useSelector(activeContactSelector);
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [formData, setFormData] = useState({
    guarantorFirstName:
      activeContact?.customer?.others?.guarantorFirstName || '',
    guarantorLastName: activeContact?.customer?.others?.guarantorLastName || '',
    contactToSpeak: activeContact?.customer?.others?.contactToSpeak || '',
    department: activeContact?.customer?.others?.department || '',
    contactGroup: activeContact?.customer?.others?.contactGroup || '',
    balance: activeContact?.customer.balance || '',
    nextPayment: activeContact?.customer.nextPayment || '',
  });
  const [initialFormData, setInitialFormData] = useState(formData);
  const [formDataForAssignedAgent, setFormDataForAssignedAgent] = useState({
    assignedAgent: activeContact?.assignedAgent?.agentName || '',
    agentTeam: activeContact?.assignedAgent?.agentTeam || '',
  });
  const [translate] = useTranslator();

  useEffect(() => {
    setInitialFormData(formData);
  }, [activeContact]);
  /**
   * Handles the click event when the edit button is clicked.
   * Sets the `isEditing` state to `true`.
   * @example
   */
  const handleEditClick = () => {
    if (isEditing) {
      // Reset form data to initial state if cancel button is clicked
      setFormData(initialFormData);
    }
    setIsEditing(!isEditing);
  }

  /**
   * Handles the click event for the save button.
   * Sets the `isEditing` state to `false`.
   * @example
   */
  const handleSaveClick = () => {
    setIsEditing(false);
    const existingCustomerProperties = Object.assign(
      {},
      activeContact?.customer
    );
    existingCustomerProperties.others = formData;
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { customer: existingCustomerProperties },
      })
    );
    const existingContactProperties = Object.assign({}, activeContact);
    existingContactProperties.assignedAgent = {
      agentId: existingContactProperties.assignedAgent?.agentId || '',
      agentName: formDataForAssignedAgent.assignedAgent,
      agentStatus: existingContactProperties.assignedAgent?.agentStatus || '',
      agentDepartment:
        existingContactProperties.assignedAgent?.agentDepartment || '',
      agentTeam: formDataForAssignedAgent.agentTeam,
    };
    dispatch(
      updateActiveContact({
        id: activeContact?.id || '',
        updates: { assignedAgent: existingContactProperties },
      })
    );
    setShowToast(true);
    setToastMessage(translate('Othersdetailssavedsuccessfully'));
    setTimeout(() => {
      setShowToast(false);
      setToastMessage('');
    }, 3000);
  };

  /**
   * Handles the click event for expanding or collapsing the custom address row.
   * @example
   */
  const handleExpandCollapseClick = () => setIsExpanded(!isExpanded);

  /**
   * Handles the change event for input elements.
   * Updates the form data with the new value.
   * @param e - The change event.
   * @example
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /**
   * Handles the change event for select elements.
   * Updates the form data with the new value.
   * @param e - The change event.
   * @example
   */
  const handleSelectChange = (e: any) => {
    const { value } = e.target;
    if (e.target.name === 'AGENT') {
      setFormDataForAssignedAgent({
        ...formDataForAssignedAgent,
        assignedAgent: value,
      });
    } else {
      setFormDataForAssignedAgent({
        ...formDataForAssignedAgent,
        agentTeam: value,
      });
    }
  };

  return (
    <div className={styles.consumerCardDetails} id="detailsTab_others_panel">
      <div className={styles.header} style={{ height: '40px' }}>
        <div style={{ display: 'flex' }}>
          {isEditing && <CcfStrokeIcon />}
          <CcfTypography className={styles.captionHeader}>
            {translate(props.headerCaption)}
          </CcfTypography>
        </div>
        <CcfBox
          display={'flex'}
          style={{ width: isEditing ? '150px' : '100px' }}
          justifyContent={'space-around'}
        >
          {isEditing ? (
            <CcfBox
              display={'flex'}
              width={'125px'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <CcfButton
                id="othersTab_BtnCancel"
                className={styles.btnStyles}
                onClick={handleEditClick}
              >
                {translate('cancel')}
              </CcfButton>
              <CcfButton
                id="othersTab_BtnSave"
                className={styles.btnStyles}
                primary
                onClick={handleSaveClick}
              >
                {translate('save')}
              </CcfButton>
            </CcfBox>
          ) : (
            <>
              {isExpanded && (
                <CcfStrokeIcon
                  id="othersTab_BtnCancel"
                  className={styles.alginIcon}
                  onClick={handleEditClick}
                />
              )}
              <CcfUpArrowIcon
                id="othersTab_BtnExpand"
                style={{ transform: 'rotate(180deg)' }}
                onClick={handleExpandCollapseClick}
              />
            </>
          )}
        </CcfBox>
      </div>
      {isExpanded && (
        <div
          className={styles.body}
          style={{ backgroundColor: isEditing ? '#ECF3F8' : '#FAFAFA' }}
        >
          <div
            style={{
              width: isEditing
                ? props.detailsMinValue
                  ? '90%'
                  : '75%'
                : props.detailsMinValue
                ? '100%'
                : '90%',
            }}
            className={styles.bodyContainer}
          >
            {isEditing && (
              <div className={styles.column}>
                <FormGroup>
                  <FormControlLabel
                    className={styles.checkBoxLabel}
                    control={
                      <Checkbox
                        checked={!guarantorAsPrimary}
                        onChange={() =>
                          setGuarantorAsPrimary(!guarantorAsPrimary)
                        }
                      />
                    }
                    label={translate("GuarantorSameAsContact")}
                  />
                </FormGroup>
              </div>
            )}
            <CcfBox display={'flex'}>
              <div className={styles.column}>
                {renderField(
                  'GUARANTOR',
                  `Mr. ${formData.guarantorFirstName} ${formData.guarantorLastName} (He\\Him)`,
                  'GUARANTORFIRSTNAME',
                  'guarantorFirstName',
                  formData.guarantorFirstName,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'DEPARTMENT',
                  formData.department,
                  'DEPARTMENT',
                  'department',
                  formData.department,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'BALANCE',
                  formData.balance,
                  'AGENT',
                  'AGENT',
                  formDataForAssignedAgent.assignedAgent,
                  isEditing ? true : false,
                  handleSelectChange,
                  props.detailsMinValue,
                  true
                )}
              </div>
              {isEditing && (
                <div className={styles.column}>
                  <div
                    style={{ visibility: 'hidden' }}
                    className={styles.field}
                  ></div>
                  <div
                    style={{ visibility: 'hidden' }}
                    className={styles.field}
                  ></div>
                  {renderField(
                    'AGENTTEAM',
                    formDataForAssignedAgent.agentTeam,
                    'AGENTTEAM',
                    'AGENT TEAM',
                    formDataForAssignedAgent.agentTeam,
                    isEditing,
                    handleSelectChange,
                    props.detailsMinValue,
                    true
                  )}
                </div>
              )}
              <div className={styles.column}>
                {renderField(
                  'CONTACTTOSPEAK',
                  formData.guarantorFirstName +
                    ' ' +
                    formData.guarantorLastName,
                  'GUARANTORLASTNAME',
                  'guarantorLastName',
                  formData.guarantorLastName,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}
                {renderField(
                  'CONTACTGROUP',
                  formData.contactGroup,
                  'CONTACTGROUP',
                  'contactGroup',
                  formData.contactGroup,
                  isEditing,
                  handleChange,
                  props.detailsMinValue
                )}

                {renderField(
                  'NEXTPAYMENT',
                  formData.nextPayment,
                  '',
                  '',
                  '',
                  isEditing ? true : false,
                  handleChange,
                  props.detailsMinValue
                )}
              </div>
            </CcfBox>
          </div>
        </div>
      )}
      <CcfBox>
        <CcfToast open={showToast} type={'success'} message={toastMessage} />
      </CcfBox>
    </div>
  );
}

/**
 * Renders a field with label and value, along with an input or select element for editing.
 * @example
 * @param label - The label for the field.
 * @param value - The value to display for the field.
 * @param name - The name attribute for the input/select element.
 * @param inputValue - The current value of the input/select element.
 * @param isEditing - A boolean indicating whether the field is in editing mode.
 * @param handleChange - The event handler for the input/select element's change event.
 * @param isSelect - A boolean indicating whether the field should render a select element instead of an input element.
 * @returns The rendered field component.
 */
function renderField(
  label: string,
  value: string,
  onEditCaption: string,
  name: string,
  inputValue: string,
  isEditing: boolean,
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  detailsMinValue: boolean,
  isSelect = false
) {
  return (
    <div
      className={styles.field}
      style={{
        visibility: name === '' && isEditing ? 'hidden' : 'visible',
        marginRight: '10px',
      }}
    >
      {!isEditing ? (
        <>
          <CcfTypography
            className={
              detailsMinValue ? styles.captionlabelMinView : styles.captionlabel
            }
            translationKey={label}
          >
            
          </CcfTypography>
          <CcfTypography
            className={
              detailsMinValue ? styles.captionValueMinView : styles.captionValue
            }
          >
            {value}
          </CcfTypography>
        </>
      ) : (
        <>
          <CcfTypography className={styles.editModeCaptionLabel} translationKey={onEditCaption.toUpperCase()}>
          </CcfTypography>
          {isSelect ? (
            <select
              id={name}
              className={styles.selectControl}
              name={name}
              disabled={name === 'AGENT' ? true : false}
              value={inputValue}
              onChange={(e: any) => handleChange(e)}
            >
              <option id={inputValue} value={inputValue}>
                {inputValue}
              </option>
              <option id="Eternals" value={'Eternals'}>
                {'Eternals'}
              </option>
            </select>
          ) : (
            <input
              id={name}
              className={styles.inputControl}
              type="text"
              name={name}
              value={inputValue}
              onChange={handleChange}
            />
          )}
        </>
      )}
    </div>
  );
}

export default CcfConsumerCardDetailsTabCustomOthersRow;
